import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { crawlerName, formatCrawlerError } from '@/lib/adminEvent';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { describeCron } from '@/lib/cron';
import {
  formatIsoDateTime,
  formatSqliteUtcDateTime,
  sqliteUtcSecondsBetween,
} from '@/lib/date';
import {
  AdminApiError,
  fetchAdminCrawlerRuns,
  fetchAdminCrawlers,
  runAdminCrawler,
} from '@/services/adminApi';
import type { AdminCrawler, CrawlerRun, CrawlerRunStatus } from '@/types/admin';

// 有爬虫在运行时按此间隔刷新，直到全部结束。
const POLL_INTERVAL_MS = 2000;
const HISTORY_LIMIT = 20;
// 详情页取名任务与数据源爬虫共用运行记录，但结果字段含义不同（候选 / 取名 / 更新 / 失败链接）。
const DETAIL_TITLE_JOB_ID = 'detail_titles';

function RunStatusBadge({ status }: { status: CrawlerRunStatus }) {
  const { t } = useTranslation('admin');
  if (status === 'running') {
    return (
      <span className="status-label text-ink-2">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
        {t('crawlers.running')}
      </span>
    );
  }
  if (status === 'success') {
    return <span className="status-label text-success">{t('crawlers.success')}</span>;
  }
  return <span className="status-label text-danger">{t('crawlers.failed')}</span>;
}

function describeTrigger(run: CrawlerRun): string {
  if (run.triggered_by === 'schedule') {
    return i18n.t('admin:crawlers.scheduledTrigger');
  }
  return i18n.t('admin:crawlers.manualTrigger', { actor: run.actor_name ?? run.actor_sub ?? i18n.t('admin:unknown') });
}

function describeRunTime(run: CrawlerRun): string {
  const started = formatSqliteUtcDateTime(run.started_at);
  if (!run.finished_at) {
    return started;
  }
  const seconds = sqliteUtcSecondsBetween(run.started_at, run.finished_at);
  if (seconds === null) {
    return started;
  }
  return i18n.t('admin:crawlers.runTime', { started, seconds });
}

function RunResult({ run }: { run: CrawlerRun }) {
  const { t } = useTranslation('admin');
  const detailJob = run.crawler_id === DETAIL_TITLE_JOB_ID;
  if (run.status === 'running') {
    return <span className="text-ink-2">{t(detailJob ? 'crawlers.detailFetching' : 'crawlers.fetching')}</span>;
  }
  if (run.status === 'failed') {
    return <span className="break-words text-danger">{formatCrawlerError(run.error)}</span>;
  }
  if (detailJob) {
    let failedLinks: ReactNode = null;
    if (run.failed_rows) {
      failedLinks = (
        <span className="text-warning" title={t('crawlers.detailFailedHint')}>
          {' '}
          · {t('crawlers.detailFailed', { count: run.failed_rows })}
        </span>
      );
    }
    return (
      <span className="text-ink-2">
        {t('crawlers.detailResult', { found: run.events_found ?? 0, saved: run.saved ?? 0, updated: run.updated ?? 0 })}
        {failedLinks}
      </span>
    );
  }
  let unparsed: ReactNode = null;
  if (run.failed_rows) {
    unparsed = (
      <span
        className="text-warning"
        title={t('crawlers.incompleteHint')}
      >
        {' '}
        · {t('crawlers.unparsed', { count: run.failed_rows })}
      </span>
    );
  }
  return (
    <span className="text-ink-2">
      {t('crawlers.result', { found: run.events_found ?? 0, saved: run.saved ?? 0, updated: run.updated ?? 0 })}
      {unparsed}
    </span>
  );
}

function LastRunCell({ run }: { run: CrawlerRun | null }) {
  const { t } = useTranslation('admin');
  if (!run) {
    return <span className="text-ink-2">{t('crawlers.neverRun')}</span>;
  }
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <RunStatusBadge status={run.status} />
        <span className="text-xs text-ink-2">{describeTrigger(run)}</span>
      </div>
      <div className="whitespace-nowrap font-mono text-xs text-ink-2">{describeRunTime(run)}</div>
    </div>
  );
}

function ScheduleCell({ crawler }: { crawler: AdminCrawler }) {
  const { t } = useTranslation('admin');
  if (!crawler.enabled) {
    return <span className="text-ink-2">{t('crawlers.disabled')}</span>;
  }
  let nextRun = '—';
  if (crawler.next_scheduled_at) {
    nextRun = formatIsoDateTime(crawler.next_scheduled_at);
  }
  return (
    <div className="space-y-1">
      <div>{describeCron(crawler.schedule)}</div>
      <div className="whitespace-nowrap font-mono text-xs text-ink-2">{t('crawlers.nextRun', { time: nextRun })}</div>
    </div>
  );
}

function isRunning(crawler: AdminCrawler): boolean {
  return crawler.last_run?.status === 'running';
}

function rememberRun(knownRuns: Map<string, CrawlerRun>, run: CrawlerRun): CrawlerRun {
  const current = knownRuns.get(run.crawler_id);
  // 在途请求不能覆盖更新的运行记录，也不能把已经结束的运行退回 running。
  if (current && (current.id > run.id || (current.id === run.id && current.status !== 'running'))) {
    return current;
  }
  knownRuns.set(run.crawler_id, run);
  return run;
}

function mergeHistory(runs: CrawlerRun[], knownRuns: Iterable<CrawlerRun>): CrawlerRun[] {
  const byId = new Map(runs.map((run) => [run.id, run]));
  for (const run of knownRuns) {
    byId.set(run.id, run);
  }
  return [...byId.values()].sort((a, b) => b.id - a.id).slice(0, HISTORY_LIMIT);
}

function CrawlersPanel() {
  const { t } = useTranslation('admin');
  const [crawlers, setCrawlers] = useState<AdminCrawler[] | null>(null);
  const [runs, setRuns] = useState<CrawlerRun[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [errors, setErrors] = useState<Array<{ crawler: AdminCrawler; message: string }>>([]);
  const [startingIds, setStartingIds] = useState<string[]>([]);
  const knownRuns = useRef(new Map<string, CrawlerRun>());
  const starting = useRef(new Set<string>());
  const mounted = useRef(false);
  const refresh = useRef<() => void>(() => {});
  const reload = useCallback(() => refresh.current(), []);

  useEffect(() => {
    mounted.current = true;
    let disposed = false;
    let inFlight = false;
    let queued = false;
    let timer: number | undefined;
    const controller = new AbortController();

    const load = async () => {
      window.clearTimeout(timer);
      if (inFlight) {
        queued = true;
        return;
      }
      inFlight = true;
      // 等待两项请求都结束，失败时也不会与下一轮请求重叠。
      const [crawlerResult, runResult] = await Promise.allSettled([
        fetchAdminCrawlers(controller.signal),
        fetchAdminCrawlerRuns(HISTORY_LIMIT, controller.signal),
      ]);
      if (disposed) {
        return;
      }
      let keepPolling = true;
      if (crawlerResult.status === 'fulfilled' && runResult.status === 'fulfilled') {
        const crawlerList = crawlerResult.value;
        const runList = runResult.value;
        for (const crawler of crawlerList) {
          if (crawler.last_run) {
            rememberRun(knownRuns.current, crawler.last_run);
          }
        }
        for (const run of runList) {
          rememberRun(knownRuns.current, run);
        }
        const latestCrawlers = crawlerList.map((crawler) => ({
          ...crawler,
          last_run: knownRuns.current.get(crawler.id) ?? crawler.last_run,
        }));
        setCrawlers(latestCrawlers);
        setRuns(mergeHistory(runList, knownRuns.current.values()));
        setLoadFailed(false);
        keepPolling = latestCrawlers.some(isRunning);
      } else {
        setLoadFailed(true);
      }
      inFlight = false;
      if (queued) {
        queued = false;
        void load();
      } else if (keepPolling) {
        timer = window.setTimeout(() => void load(), POLL_INTERVAL_MS);
      }
    };
    refresh.current = () => void load();
    void load();
    return () => {
      disposed = true;
      mounted.current = false;
      refresh.current = () => {};
      window.clearTimeout(timer);
      controller.abort();
    };
  }, []);

  // 立即保留已接受的运行记录，即使后续状态刷新失败也继续跟踪。
  const startCrawler = async (crawler: AdminCrawler): Promise<{ crawler: AdminCrawler; message: string } | null> => {
    if (!mounted.current || starting.current.has(crawler.id)
      || knownRuns.current.get(crawler.id)?.status === 'running') {
      return null;
    }
    starting.current.add(crawler.id);
    setStartingIds((ids) => [...ids, crawler.id]);
    try {
      const run = await runAdminCrawler(crawler.id);
      if (mounted.current) {
        const latest = rememberRun(knownRuns.current, run);
        setCrawlers((current) => current?.map((item) => (
          item.id === crawler.id ? { ...item, last_run: latest } : item
        )) ?? null);
        setRuns((current) => mergeHistory(current, [latest]));
      }
      return null;
    } catch (reason) {
      let message = 'crawlers.startFailed';
      if (reason instanceof AdminApiError) {
        message = reason.message;
      }
      return { crawler, message };
    } finally {
      starting.current.delete(crawler.id);
      if (mounted.current) {
        setStartingIds((ids) => ids.filter((id) => id !== crawler.id));
        reload();
      }
    }
  };

  const handleStart = async (crawler: AdminCrawler) => {
    const failure = await startCrawler(crawler);
    if (mounted.current) {
      setErrors(failure ? [failure] : []);
    }
  };

  const idleCrawlers = (crawlers ?? []).filter(
    (crawler) => crawler.enabled && !isRunning(crawler),
  );

  const handleStartAll = async () => {
    const failures: Array<{ crawler: AdminCrawler; message: string }> = [];
    for (const crawler of idleCrawlers) {
      if (!mounted.current) {
        return;
      }
      const failure = await startCrawler(crawler);
      if (failure) {
        failures.push(failure);
      }
    }
    if (mounted.current) {
      setErrors(failures);
    }
  };

  const crawlerNames: Record<string, string> = {};
  for (const crawler of crawlers ?? []) {
    crawlerNames[crawler.id] = crawlerName(crawler);
  }

  let body = (
    <p className="kicker m-0 py-12" aria-live="polite">{t('loading')}</p>
  );
  if (loadFailed && !crawlers) {
    body = <p className="m-0 py-12 text-sm text-ink-2">{t('crawlers.loadFailed')}</p>;
  } else if (crawlers && crawlers.length === 0) {
    body = <p className="m-0 py-12 text-sm text-ink-2">{t('crawlers.empty')}</p>;
  } else if (crawlers) {
    body = (
      <div className="overflow-x-auto">
        <table className="table-editorial">
          <thead>
            <tr>
              <th>{t('nav.crawlers')}</th>
              <th className="whitespace-nowrap">{t('crawlers.schedule')}</th>
              <th className="whitespace-nowrap">{t('crawlers.lastRun')}</th>
              <th>{t('crawlers.resultColumn')}</th>
              <th className="pr-0 text-right">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {crawlers.map((crawler) => (
              <tr key={crawler.id}>
                <td className="min-w-[200px]">
                  <div className="text-ink">{crawlerName(crawler)}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-ink-2">
                    <code className="font-mono">{crawler.id}</code>
                    {crawler.url && (
                      <a
                        href={crawler.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-0.5 hover:text-ink"
                      >
                        {t('crawlers.sourcePage')}
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                </td>
                <td>
                  <ScheduleCell crawler={crawler} />
                </td>
                <td>
                  <LastRunCell run={crawler.last_run} />
                </td>
                <td className="min-w-[180px] text-xs">
                  {crawler.last_run && <RunResult run={crawler.last_run} />}
                  {!crawler.last_run && <span className="text-ink-2">—</span>}
                </td>
                <td className="whitespace-nowrap pr-0 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={
                      !crawler.enabled || isRunning(crawler) || startingIds.includes(crawler.id)
                    }
                    onClick={() => handleStart(crawler)}
                  >
                    {t('crawlers.runNow')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  let history = <p className="m-0 py-6 text-sm text-ink-2">{t('crawlers.noHistory')}</p>;
  if (runs.length > 0) {
    history = (
      <ul className="m-0 list-none divide-y divide-rule p-0">
        {runs.map((run) => (
          <li key={run.id} className="space-y-1 py-3 text-sm">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <RunStatusBadge status={run.status} />
              <span className="text-ink">{crawlerNames[run.crawler_id] ?? run.crawler_id}</span>
              <span className="text-xs text-ink-2">
                {describeTrigger(run)} · <span className="font-mono">{describeRunTime(run)}</span>
              </span>
            </div>
            <div className="text-xs">
              <RunResult run={run} />
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-rule pb-5">
        <div className="flex flex-col gap-2">
          <h1 className="m-0 text-[1.75rem] md:text-[2rem]">{t('crawlers.title')}</h1>
          <p className="m-0 max-w-[64ch] text-sm leading-relaxed text-ink-2">
            {t('crawlers.description')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={reload}>
            {t('crawlers.refresh')}
          </Button>
          <Button
            size="sm"
            disabled={idleCrawlers.length === 0 || startingIds.length > 0}
            onClick={handleStartAll}
          >
            {t('crawlers.runAll')}
          </Button>
        </div>
      </header>

      {errors.length > 0 && (
        <p role="alert" className="callout callout-danger m-0 mt-4">
          {errors.map(({ crawler, message }) => t('crawlers.namedError', { name: crawlerName(crawler), message: t(message, { defaultValue: message }) })).join(t('crawlers.errorSeparator'))}
        </p>
      )}

      {loadFailed && crawlers && (
        <p role="alert" className="callout callout-warning m-0 mt-4">
          {t('crawlers.refreshFailed')}
        </p>
      )}

      <section className="mt-6 border-t-2 border-ink">{body}</section>

      <section className="mt-10">
        <div className="section-head">
          <h2 className="m-0">{t('crawlers.history')}</h2>
          <span className="kicker">{t('crawlers.historyDescription', { count: HISTORY_LIMIT })}</span>
        </div>
        {history}
      </section>
    </div>
  );
}

export function AdminCrawlersPage() {
  return (
    <AdminLayout>
      <CrawlersPanel />
    </AdminLayout>
  );
}
